import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Badge } from '@/shared/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { toast } from 'sonner';
import {
  Search,
  Plus,
  Calendar,
  Phone,
  Mail,
  Users,
  CheckSquare,
  Clock,
  CheckCircle2,
  Play,
  MoreVertical,
  Link as LinkIcon,
  RefreshCw,
  FileText } from
'lucide-react';

const API_URL = API_BASE || (
  typeof window !== 'undefined' && ['localhost', '127.0.0.1'].includes(window.location.hostname)
    ? 'http://localhost:8001'
    : ''
);

const getAuthHeaders = () => {
  const token = localStorage.getItem('ezerpToken');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

const typeIcons = {
  call: Phone,
  meeting: Users,
  email: Mail,
  task: CheckSquare,
  note: FileText
};

const typeColors = {
  call: 'bg-teal-100 text-teal-700 dark:bg-teal-900/50 dark:text-teal-400',
  meeting: 'bg-orange-100 text-orange-700 dark:bg-orange-900/50 dark:text-orange-400',
  email: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-400',
  task: 'bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-400',
  note: 'bg-gray-100 text-gray-700 dark:bg-gray-900/50 dark:text-gray-400'
};

export const Activities = () => {const { t } = useTranslation("crm");
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('upcoming');
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLogActivity, setShowLogActivity] = useState(false);

  const fetchActivities = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/crm/activities`;
      const params = [];
      if (activeTab === 'upcoming') {
        params.push('completed=false');
      } else if (activeTab === 'completed') {
        params.push('completed=true');
      }
      if (params.length > 0) {
        url += `?${params.join('&')}`;
      }
      const response = await fetch(url, {
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to fetch activities');
      const data = await response.json();
      setActivities(data);
    } catch (err) {
      toast.error('Failed to load activities');
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async (activityId) => {
    try {
      const response = await fetch(`${API_URL}/api/crm/activities/${activityId}/complete`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to complete activity');
      toast.success('Activity completed');
      fetchActivities();
    } catch (err) {
      toast.error('Failed to complete activity');
    }
  };

  useEffect(() => {
    fetchActivities();
  }, [activeTab]);

  const filteredActivities = activities.filter((activity) => {
    if (!searchTerm) return true;
    return activity.subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    activity.description?.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const stats = {
    total: activities.length,
    scheduled: activities.filter((a) => !a.completed).length,
    completed: activities.filter((a) => a.completed).length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-500" />
      </div>);

  }

  return (
    <div className="space-y-6" data-testid="crm-activities">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk']">{t("activities", "Activities")}

          </h1>
          <p className="text-muted-foreground mt-1">{t("trackCallsMeetings", "Track calls, meetings, emails, and tasks")}

          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchActivities} className="gap-2">
            <RefreshCw className="h-4 w-4" />{t("refresh", "Refresh")}

          </Button>
          <Button className="gap-2 bg-teal-600 hover:bg-teal-700" onClick={() => setShowLogActivity(true)}>
            <Plus className="h-4 w-4" />{t("logActivity", "Log Activity")}

          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-4">
        <Card className="card-elevated border-l-4 border-l-teal-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-teal-500/10">
                <Calendar className="h-5 w-5 text-teal-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t("totalActivities", "Total Activities")}</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-blue-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/10">
                <Clock className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t("scheduled", "Scheduled")}</p>
                <p className="text-2xl font-bold">{stats.scheduled}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-emerald-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-500/10">
                <CheckCircle2 className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t("completed", "Completed")}</p>
                <p className="text-2xl font-bold">{stats.completed}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-amber-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-500/10">
                <Play className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t("inProgress", "In Progress")}</p>
                <p className="text-2xl font-bold">{activities.filter((a) => !a.completed).length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters and List */}
      <Card className="card-elevated">
        <CardHeader className="pb-4">
          <div className="flex flex-col sm:flex-row gap-4 justify-between">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search activities..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10" />
              
            </div>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList>
                <TabsTrigger value="upcoming">{t("upcoming", "Upcoming")}</TabsTrigger>
                <TabsTrigger value="completed">{t("completed", "Completed")}</TabsTrigger>
                <TabsTrigger value="all">{t("all", "All")}</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {filteredActivities.map((activity) => {
              const TypeIcon = typeIcons[activity.type] || Calendar;
              return (
                <Card key={activity.id} className="border hover:shadow-md transition-all">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      <div className={`p-3 rounded-lg ${(typeColors[activity.type] || typeColors.task).split(' ')[0]}`}>
                        <TypeIcon className={`h-5 w-5 ${(typeColors[activity.type] || typeColors.task).split(' ')[1]}`} />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold">{activity.subject}</h3>
                          <Badge className={activity.completed ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}>
                            {activity.completed && <CheckCircle2 className="h-3 w-3 mr-1" />}
                            {!activity.completed && <Clock className="h-3 w-3 mr-1" />}
                            {activity.completed ? 'completed' : 'pending'}
                          </Badge>
                        </div>
                        {activity.description &&
                        <p className="text-sm text-muted-foreground mb-2">{activity.description}</p>
                        }
                        
                        <div className="flex flex-wrap items-center gap-4 text-sm">
                          {activity.contact_id &&
                          <div className="flex items-center gap-1.5">
                              <LinkIcon className="h-3.5 w-3.5 text-muted-foreground" />
                              <span className="text-teal-600">{t("contact", "Contact #")}{activity.contact_id}</span>
                            </div>
                          }
                          <div className="flex items-center gap-1.5">
                            <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                            <span>{new Date(activity.due_date).toLocaleDateString()}</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <Users className="h-3.5 w-3.5 text-muted-foreground" />
                            <span>{activity.owner}</span>
                          </div>
                        </div>
                      </div>

                      <div className="flex gap-1">
                        {!activity.completed &&
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleComplete(activity.id)}>
                          
                            <CheckCircle2 className="h-4 w-4 mr-1" />{t("complete", "Complete")}

                        </Button>
                        }
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>);

            })}
          </div>

          {filteredActivities.length === 0 &&
          <div className="py-12 text-center">
              <Calendar className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">{t("noActivitiesFound", "No activities found")}</h3>
              <p className="text-muted-foreground">{t("tryAdjustingYour", "Try adjusting your search or filter criteria")}</p>
            </div>
          }
        </CardContent>
      </Card>
      {showLogActivity && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div role="dialog" aria-modal="true" aria-label="Log Activity" className="w-full max-w-2xl rounded-lg border bg-background shadow-xl">
            <div className="border-b p-5">
              <h2 className="text-lg font-semibold">Log Activity</h2>
              <p className="text-sm text-muted-foreground">Record a call, meeting, email, task, or note for the CRM timeline.</p>
            </div>
            <div className="grid gap-4 p-5 sm:grid-cols-2">
              <Input placeholder="Subject" />
              <Input placeholder="Type" defaultValue="call" />
              <Input placeholder="Contact or account" />
              <Input placeholder="Due date" />
              <Input placeholder="Owner" />
              <Input placeholder="Description" />
            </div>
            <div className="flex justify-end gap-2 border-t p-5">
              <Button variant="outline" onClick={() => setShowLogActivity(false)}>Cancel</Button>
              <Button className="bg-teal-600 hover:bg-teal-700">Save Activity</Button>
            </div>
          </div>
        </div>
      )}
    </div>);

};
